// src/hooks/useDataSells.js
import { useState, useEffect, useCallback } from 'react';
import {
    uploadDataFile,
    validarArchivo,
    mapearCamposConIA,
    obtenerCamposDisponibles,
    crearMapeoManual,
    actualizarMapeoManual,
    procesarVentas,
    obtenerVentas,
    obtenerVentaPorId,
    obtenerArchivosSubidos,
    obtenerArchivosPorMarca,
    obtenerArchivoPorId,
    eliminarArchivo,
    enriquecerTickets,
    contarVentasPorArchivo
} from '../helpers/dataSellsService';

/**
 * Custom hook para gestión de ventas desde archivos CSV/Excel
 */
export const useDataSells = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ventas, setVentas] = useState([]);
    const [archivosSubidos, setArchivosSubidos] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [notificationCounter, setNotificationCounter] = useState(0);

    // ==================== NOTIFICACIONES ====================

    const addNotification = useCallback((type, message, details = null) => {
        // Generar ID único combinando timestamp, contador y número aleatorio
        const uniqueId = `${Date.now()}-${notificationCounter}-${Math.random().toString(36).substr(2, 9)}`;
        setNotificationCounter(prev => prev + 1);

        const notification = {
            id: uniqueId,
            type, // 'success', 'error', 'warning', 'info'
            message,
            details,
            timestamp: new Date().toISOString()
        };
        setNotifications(prev => [notification, ...prev]);

        // Auto-eliminar notificaciones de éxito después de 5 segundos
        if (type === 'success') {
            setTimeout(() => {
                removeNotification(notification.id);
            }, 5000);
        }
    }, [notificationCounter]);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // ==================== PASO 1: SUBIR ARCHIVO ====================

    const subirArchivo = useCallback(async (file, marcaId, tiendasIds) => {
        setLoading(true);
        setError(null);

        try {
            const archivoSubido = await uploadDataFile(file, marcaId, tiendasIds);

            // Añadir a la lista de archivos subidos
            setArchivosSubidos(prev => [archivoSubido, ...prev]);

            // Verificar si el backend procesará automáticamente
            if (archivoSubido.procesamientoAutomatico) {
                addNotification(
                    'info',
                    `✓ Archivo "${file.name}" subido exitosamente`,
                    {
                        archivoId: archivoSubido.id,
                        mensaje: 'El sistema está procesando tu archivo automáticamente en segundo plano. Te notificaremos cuando esté listo o si necesita tu intervención.'
                    }
                );

                // Iniciar polling para verificar el estado (cada 10 segundos por 5 minutos)
                iniciarPollingEstadoArchivo(archivoSubido.id, marcaId, 10000, 30);
            } else {
                addNotification(
                    'success',
                    `Archivo "${file.name}" subido exitosamente`,
                    { archivoId: archivoSubido.id }
                );
            }

            return archivoSubido;
        } catch (err) {
            const errorMsg = err.message || 'Error al subir el archivo';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== POLLING PARA PROCESAMIENTO AUTOMÁTICO ====================

    const iniciarPollingEstadoArchivo = useCallback((archivoId, marcaId, intervalo = 10000, intentosMaximos = 30) => {
        let intentos = 0;
        let ultimoEstado = null;
        let erroresConsecutivos = 0;

        const intervaloId = setInterval(async () => {
            intentos++;

            try {
                // En lugar de obtener un archivo específico (que causa error de permisos),
                // recargar la lista completa y buscar el archivo
                const archivos = await obtenerArchivosSubidos({ marca: marcaId });
                const archivo = archivos.find(a => (a.id || a._id) === archivoId);

                if (!archivo) {
                    // Si no se encuentra el archivo después de varios intentos, detener
                    erroresConsecutivos++;
                    if (erroresConsecutivos > 3) {
                        clearInterval(intervaloId);
                        console.log('Archivo no encontrado después de 3 intentos, deteniendo polling');
                    }
                    return;
                }

                // Resetear contador de errores si encontramos el archivo
                erroresConsecutivos = 0;

                // Verificar si cambió el estado
                const nuevoEstado = determinarEstadoArchivo(archivo);

                if (nuevoEstado !== ultimoEstado) {
                    ultimoEstado = nuevoEstado;

                    // Notificar según el estado
                    switch (nuevoEstado) {
                        case 'procesado':
                            addNotification(
                                'success',
                                `✓ Archivo procesado completamente`,
                                {
                                    archivoId,
                                    mensaje: 'Todas las ventas han sido importadas exitosamente. Los productos faltantes han sido creados automáticamente.'
                                }
                            );
                            clearInterval(intervaloId);
                            // Recargar archivos subidos para actualizar la UI
                            cargarArchivosSubidos(marcaId);
                            break;

                        case 'requiere_mapeo_manual':
                            addNotification(
                                'warning',
                                `⚠ Mapeo manual requerido`,
                                {
                                    archivoId,
                                    mensaje: 'El sistema no pudo mapear todos los campos automáticamente. Por favor, completa el mapeo manualmente.'
                                }
                            );
                            clearInterval(intervaloId);
                            // Recargar archivos subidos para actualizar la UI
                            cargarArchivosSubidos(marcaId);
                            break;

                        case 'error':
                            addNotification(
                                'error',
                                `Error al procesar archivo`,
                                {
                                    archivoId,
                                    mensaje: 'Ocurrió un error durante el procesamiento. Por favor, intenta procesarlo manualmente.'
                                }
                            );
                            clearInterval(intervaloId);
                            break;
                    }
                }

                // Detener después de intentos máximos (5 minutos si intervalo = 10s)
                if (intentos >= intentosMaximos) {
                    clearInterval(intervaloId);
                    console.log('Polling detenido: alcanzado límite de intentos');
                }

            } catch (error) {
                console.error('Error en polling de estado:', error);
                erroresConsecutivos++;

                // Si hay muchos errores consecutivos, detener el polling
                if (erroresConsecutivos > 5) {
                    clearInterval(intervaloId);
                    console.log('Polling detenido: demasiados errores consecutivos');
                }
            }
        }, intervalo);

        // Guardar el intervalo para poder cancelarlo si es necesario
        return intervaloId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addNotification]);

    // Determinar el estado del archivo basándose en sus propiedades
    const determinarEstadoArchivo = (archivo) => {
        // Lógica para determinar el estado basándose en:
        // - Si tiene ventas asociadas → 'procesado'
        // - Si tiene mapeo parcial → 'requiere_mapeo_manual'
        // - Si tiene errores → 'error'
        // - Por defecto → 'procesando'

        // Por ahora, usamos una lógica simple
        // Esto debería mejorarse según la respuesta real del backend

        if (archivo.ventasProcesadas > 0) {
            return 'procesado';
        }

        if (archivo.estadoProcesamiento === 'error') {
            return 'error';
        }

        if (archivo.requiereMapeoManual) {
            return 'requiere_mapeo_manual';
        }

        return 'procesando';
    };

    // ==================== PASO 2: VALIDAR ====================

    const validarArchivoSubido = useCallback(async (archivoId, silencioso = false) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await validarArchivo(archivoId);

            // No mostrar notificaciones si es en modo silencioso (llamada desde modal)
            if (!silencioso) {
                if (resultado.todosMapeados) {
                    addNotification(
                        'success',
                        'Todos los campos están mapeados. Puede procesar el archivo.',
                        { archivoId }
                    );
                } else {
                    addNotification(
                        'warning',
                        `Faltan ${resultado.camposFaltantes.length} campo(s) por mapear`,
                        {
                            archivoId,
                            camposFaltantes: resultado.camposFaltantes
                        }
                    );
                }
            }

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al validar el archivo';
            setError(errorMsg);
            if (!silencioso) {
                addNotification('error', errorMsg);
            }
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== MAPEO AUTOMÁTICO CON IA ====================

    const mapearConIA = useCallback(async (archivoId) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await mapearCamposConIA(archivoId);

            if (resultado.camposNoReconocidos && resultado.camposNoReconocidos.length > 0) {
                addNotification(
                    'warning',
                    `IA mapeó la mayoría de campos, pero ${resultado.camposNoReconocidos.length} campo(s) requieren mapeo manual`,
                    {
                        archivoId,
                        camposNoReconocidos: resultado.camposNoReconocidos
                    }
                );
            } else {
                addNotification(
                    'success',
                    'IA mapeó todos los campos exitosamente. Puede procesar el archivo.',
                    { archivoId }
                );
            }

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al mapear campos con IA';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== MAPEO MANUAL ====================

    const obtenerCampos = useCallback(async (archivoId) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await obtenerCamposDisponibles(archivoId);
            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al obtener campos disponibles';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    const crearMapeo = useCallback(async (
        archivoId,
        sistemaOrigen,
        mapeoCampos,
        configuracionFormato
    ) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await crearMapeoManual(
                archivoId,
                sistemaOrigen,
                mapeoCampos,
                configuracionFormato
            );

            addNotification(
                'success',
                'Mapeo manual creado exitosamente',
                { archivoId }
            );

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al crear mapeo manual';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    const actualizarMapeo = useCallback(async (
        archivoId,
        mapeoCampos,
        configuracionFormato
    ) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await actualizarMapeoManual(
                archivoId,
                mapeoCampos,
                configuracionFormato
            );

            addNotification(
                'success',
                'Mapeo actualizado exitosamente',
                { archivoId }
            );

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al actualizar mapeo';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== PROCESAR VENTAS ====================

    const procesarArchivoVentas = useCallback(async (archivoId) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await procesarVentas(archivoId);

            // Construir mensaje principal con resumen
            let mensajePrincipal = `✓ ${resultado.ventasCreadas} venta(s) procesada(s) exitosamente`;

            if (resultado.ventasDuplicadas > 0) {
                mensajePrincipal += ` | ${resultado.ventasDuplicadas} duplicada(s) omitida(s)`;
            }

            // Notificación principal de éxito
            addNotification(
                'success',
                mensajePrincipal,
                {
                    archivoId,
                    ventasCreadas: resultado.ventasCreadas,
                    ventasDuplicadas: resultado.ventasDuplicadas
                }
            );

            // Filtrar y mostrar solo errores importantes (no informativos)
            if (resultado.errores && resultado.errores.length > 0) {
                const erroresImportantes = resultado.errores.filter(error => {
                    const errorLower = error.toLowerCase();
                    // Filtrar mensajes informativos del enriquecimiento automático
                    // y mensajes que ya se mostraron en el resumen
                    return !errorLower.includes('enriquecimiento automático') &&
                        !errorLower.includes('duplicada(s) que fueron omitidas') &&
                        !errorLower.includes('duplicada(s) fueron omitidas') &&
                        !errorLower.includes('ticket(s) actualizado');
                });

                // Solo mostrar errores realmente importantes
                if (erroresImportantes.length > 0) {
                    // Agrupar errores similares
                    const erroresUnicos = [...new Set(erroresImportantes)];

                    // Si hay muchos errores, mostrar solo un resumen
                    if (erroresUnicos.length > 3) {
                        addNotification(
                            'warning',
                            `⚠ Se encontraron ${erroresUnicos.length} advertencias durante el procesamiento`,
                            { archivoId, errores: erroresUnicos }
                        );
                    } else {
                        // Mostrar errores individuales si son pocos
                        erroresUnicos.forEach(error => {
                            addNotification('warning', error, { archivoId });
                        });
                    }
                }
            }

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al procesar ventas';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== CONSULTAS DE VENTAS ====================

    const cargarVentas = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);

        try {
            const ventasData = await obtenerVentas(filters);
            setVentas(Array.isArray(ventasData) ? ventasData : []);
            return ventasData;
        } catch (err) {
            const errorMsg = err.message || 'Error al cargar ventas';
            setError(errorMsg);
            console.error('Error en cargarVentas:', errorMsg);
            // No mostrar notificación en carga inicial, solo en caso de error real
            // addNotification('error', errorMsg);
            setVentas([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    const cargarVentaPorId = useCallback(async (ventaId) => {
        setLoading(true);
        setError(null);

        try {
            const venta = await obtenerVentaPorId(ventaId);
            return venta;
        } catch (err) {
            const errorMsg = err.message || 'Error al cargar venta';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    const cargarArchivosSubidos = useCallback(async (marcaId = null) => {
        setLoading(true);
        setError(null);

        try {
            const archivos = marcaId
                ? await obtenerArchivosPorMarca(marcaId)
                : await obtenerArchivosSubidos();

            setArchivosSubidos(Array.isArray(archivos) ? archivos : []);
            return archivos;
        } catch (err) {
            const errorMsg = err.message || 'Error al cargar archivos';
            setError(errorMsg);
            setArchivosSubidos([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // ==================== ELIMINAR ARCHIVO ====================

    const eliminarArchivoSubido = useCallback(async (archivoId) => {
        setLoading(true);
        setError(null);

        try {
            await eliminarArchivo(archivoId);

            // Eliminar del estado local
            setArchivosSubidos(prev => prev.filter(a => (a.id || a._id) !== archivoId));

            addNotification(
                'success',
                'Archivo eliminado exitosamente',
                { archivoId }
            );

            return true;
        } catch (err) {
            const errorMsg = err.message || 'Error al eliminar archivo';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    // ==================== ENRIQUECIMIENTO ====================

    const enriquecer = useCallback(async (marcaId, fechaDesde = null, fechaHasta = null) => {
        setLoading(true);
        setError(null);

        try {
            const resultado = await enriquecerTickets(marcaId, fechaDesde, fechaHasta);

            addNotification(
                'success',
                `Enriquecimiento completado: ${resultado.ticketsActualizados} ticket(s) actualizado(s), ${resultado.productosCreados} producto(s) creado(s)`,
                {
                    ticketsActualizados: resultado.ticketsActualizados,
                    productosCreados: resultado.productosCreados,
                    categoriasCreadas: resultado.categoriasCreadas
                }
            );

            return resultado;
        } catch (err) {
            const errorMsg = err.message || 'Error al enriquecer tickets';
            setError(errorMsg);
            addNotification('error', errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    return {
        // Estado
        loading,
        error,
        ventas,
        archivosSubidos,
        notifications,

        // Métodos de subida y procesamiento
        subirArchivo,
        validarArchivoSubido,
        mapearConIA,
        obtenerCampos,
        crearMapeo,
        actualizarMapeo,
        procesarArchivoVentas,

        // Métodos de consulta
        cargarVentas,
        cargarVentaPorId,
        cargarArchivosSubidos,
        eliminarArchivoSubido,

        // Enriquecimiento
        enriquecer,

        // Gestión de notificaciones
        addNotification,
        removeNotification,
        clearNotifications,

        // Limpiar error
        clearError: () => setError(null)
    };
};

export default useDataSells;

